/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Enhanced Logger with Sentry Integration
 *
 * Features:
 * - Environment-aware logging
 * - Sentry breadcrumb tracking
 * - Structured log formatting
 * - Performance timing helpers
 */

import * as Sentry from "@sentry/react";

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogContext {
  [key: string]: unknown;
}

const isProduction = import.meta.env.VITE_REACT_ENV === "production";

/**
 * Format log message with optional context
 * Note: Reserved for future structured logging
 */
const _formatMessage = (message: unknown, context?: LogContext): string => {
  const baseMessage =
    typeof message === "string" ? message : JSON.stringify(message);

  if (context && Object.keys(context).length > 0) {
    return `${baseMessage} ${JSON.stringify(context)}`;
  }

  return baseMessage;
};

/**
 * Add Sentry breadcrumb for tracking user actions
 */
const addBreadcrumb = (
  level: LogLevel,
  message: string,
  category: string = "log",
  data?: Record<string, unknown>,
): void => {
  if (isProduction) {
    Sentry.addBreadcrumb({
      category,
      message,
      level: level === "warn" ? "warning" : level,
      data,
      timestamp: Date.now() / 1000,
    });
  }
};

export const logger = {
  /**
   * Log a general message (development only)
   */
  log: (message: unknown, ...args: unknown[]): void => {
    if (!isProduction) {
      console.log(`[Logger] ${message}`, ...args);
    }
  },

  /**
   * Log debug information (development only)
   */
  debug: (message: unknown, ...args: unknown[]): void => {
    if (!isProduction) {
      console.log(`[Logger][DEBUG] ${message}`, ...args);
    }
  },

  /**
   * Log informational message
   */
  info: (message: unknown, ...args: unknown[]): void => {
    const formatted =
      typeof message === "string" ? message : JSON.stringify(message);

    if (!isProduction) {
      console.info(`[Logger][INFO] ${formatted}`, ...args);
    }

    // Add breadcrumb in production
    addBreadcrumb("info", formatted, "info");
  },

  /**
   * Log warning message
   */
  warn: (message: unknown, ...args: unknown[]): void => {
    const formatted =
      typeof message === "string" ? message : JSON.stringify(message);

    if (!isProduction) {
      console.warn(`[Logger][WARN] ${formatted}`, ...args);
    }

    // Always track warnings as breadcrumbs
    addBreadcrumb("warn", formatted, "warning");
  },

  /**
   * Log error message
   */
  error: (message: unknown, ...args: unknown[]): void => {
    const formatted =
      typeof message === "string" ? message : JSON.stringify(message);

    if (!isProduction) {
      console.error(`[Logger][ERROR] ${formatted}`, ...args);
    }

    // Always track errors
    addBreadcrumb("error", formatted, "error");

    // Report to Sentry in production if error object provided
    if (isProduction && args.length > 0 && args[0] instanceof Error) {
      Sentry.captureException(args[0]);
    }
  },

  /**
   * Track a user action (useful for debugging user flows)
   */
  action: (action: string, details?: Record<string, unknown>): void => {
    if (!isProduction) {
      console.log(`[Logger][ACTION] ${action}`, details);
    }

    addBreadcrumb("info", action, "user-action", details);
  },

  /**
   * Track API request/response
   */
  api: (
    method: string,
    url: string,
    status?: number,
    duration?: number,
  ): void => {
    const message = `${method} ${url} ${status || "pending"} ${duration ? `(${duration}ms)` : ""}`;

    if (!isProduction) {
      console.log(`[Logger][API] ${message}`);
    }

    addBreadcrumb("info", message, "http", {
      method,
      url,
      status,
      duration,
    });
  },

  /**
   * Start a performance timing
   */
  startTimer: (label: string): (() => void) => {
    const start = performance.now();

    return () => {
      const duration = Math.round(performance.now() - start);

      if (!isProduction) {
        console.log(`[Logger][PERF] ${label}: ${duration}ms`);
      }

      addBreadcrumb("info", `${label}: ${duration}ms`, "performance", {
        label,
        duration,
      });
    };
  },

  /**
   * Create a scoped logger with a prefix
   */
  scope: (prefix: string) => ({
    debug: (message: unknown, ...args: unknown[]) =>
      logger.debug(`[${prefix}] ${message}`, ...args),
    info: (message: unknown, ...args: unknown[]) =>
      logger.info(`[${prefix}] ${message}`, ...args),
    warn: (message: unknown, ...args: unknown[]) =>
      logger.warn(`[${prefix}] ${message}`, ...args),
    error: (message: unknown, ...args: unknown[]) =>
      logger.error(`[${prefix}] ${message}`, ...args),
  }),

  /**
   * Group related logs together
   */
  group: (label: string): void => {
    if (!isProduction) {
      console.group(`[Logger] ${label}`);
    }
  },

  /**
   * End a log group
   */
  groupEnd: (): void => {
    if (!isProduction) {
      console.groupEnd();
    }
  },
};
